import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  approvalApi,
  assignmentsApi,
  dashboardApi,
  returnedWorkApi,
  reviewApi,
  samplesApi,
  type DashboardSummary,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { toneForSampleStatus } from '../samples/status.js';
import { AssignmentDates } from './AssignmentDates.js';
import { formatCalendarDate, toneForAssignmentStatus } from './status.js';
import {
  CountRows,
  HorizontalBars,
  ViewTabPanel,
  ViewTabs,
  WorkCell,
  type BarDatum,
  type CountRow,
} from './work-ui.js';

/**
 * The home dashboard — decision D93, redrawn for D278.
 *
 * COUNTS, NOT CLAIMS. Each figure is how many records are in a stated state
 * today, and each links to the list it counts. There is no percentage, trend
 * or forecast, because none of those is a fact the system holds. The bars are
 * a drawing of those same counts, with the numbers printed on them and a
 * table equivalent; their length means nothing beyond the count.
 *
 * A section the backend returns as `null` is one this person may not see, and
 * it is simply absent here — it is never drawn as a zero, which would assert
 * that nothing is waiting. The same rule decides which queues get a tab.
 */
export function HomePage() {
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const summary = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.summary(),
  });

  if (summary.isLoading) return <LoadingState label={t('common.loading')} />;
  if (summary.error) {
    return <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />;
  }
  const data = summary.data;
  if (!data) return null;

  const count = (value: number) => formatNumber(value, { locale }) ?? String(value);
  const date = formatCalendarDate(data.asOf, locale) ?? data.asOf;

  /*
   * OPEN WORK BY STAGE. Only figures the summary actually holds, in the order
   * work moves. Where the reviewer's own queue count exists it is the review
   * stage; otherwise the work list's "awaiting review" figure stands in, so the
   * same stage is never drawn twice.
   */
  const stages: BarDatum[] = [];
  if (data.samples) {
    stages.push({
      key: 'inspect',
      label: t('status.sample.awaiting_inspection'),
      value: data.samples.awaitingInspection,
      to: '/samples',
    });
  }
  if (data.work) {
    stages.push(
      { key: 'unassigned', label: t('assignmentView.unassigned'), value: data.work.unassigned, to: '/work?view=unassigned' },
      { key: 'progress', label: t('assignmentView.in_progress'), value: data.work.inProgress, to: '/work?view=in_progress' },
    );
  }
  if (typeof data.awaitingReview === 'number') {
    stages.push({ key: 'review', label: t('home.awaitingReview'), value: data.awaitingReview, to: '/review' });
  } else if (data.work) {
    stages.push({
      key: 'review',
      label: t('assignmentView.awaiting_review'),
      value: data.work.awaitingReview,
      to: '/work?view=awaiting_review',
    });
  }
  if (typeof data.awaitingApproval === 'number') {
    stages.push({ key: 'approve', label: t('home.awaitingApproval'), value: data.awaitingApproval, to: '/approval' });
  }
  if (typeof data.returnedToMe === 'number') {
    // A state that needs the person's attention, so it carries the state colour.
    stages.push({
      key: 'returned',
      label: t('home.returnedToMe'),
      value: data.returnedToMe,
      to: '/returned',
      tone: 'warning',
    });
  }

  /* NEEDS ATTENTION — the figures that mean something is late, held or blocked. */
  const attention: CountRow[] = [];
  if (data.work) {
    attention.push(
      { label: t('assignmentView.delayed'), value: count(data.work.delayed), to: '/work?view=delayed', attention: 'warning', isAboveZero: data.work.delayed > 0 },
      { label: t('assignmentView.stopped'), value: count(data.work.stopped), to: '/work?view=stopped', attention: 'warning', isAboveZero: data.work.stopped > 0 },
    );
    // The work list's own "awaiting review" figure, kept when the review stage
    // above is the reviewer's queue — it counts all of the laboratory's work.
    if (typeof data.awaitingReview === 'number') {
      attention.push({
        label: t('assignmentView.awaiting_review'),
        value: count(data.work.awaitingReview),
        to: '/work?view=awaiting_review',
      });
    }
  }
  if (data.samples) {
    attention.push({ label: t('status.sample.held'), value: count(data.samples.held), to: '/samples', attention: 'warning', isAboveZero: data.samples.held > 0 });
  }
  if (data.equipment) {
    attention.push(
      { label: t('home.equipmentBlocked'), value: count(data.equipment.blockedToday), to: '/equipment', attention: 'danger', isAboveZero: data.equipment.blockedToday > 0 },
      { label: t('home.equipmentInService'), value: count(data.equipment.inService), to: '/equipment' },
    );
  }
  const mine: CountRow[] = data.myTasks
    ? [
        { label: t('home.openTasks'), value: count(data.myTasks.open), to: '/my-tasks' },
        { label: t('home.delayedTasks'), value: count(data.myTasks.delayed), to: '/my-tasks', attention: 'warning', isAboveZero: data.myTasks.delayed > 0 },
      ]
    : [];

  const role = user?.role ? t(`roles.${user.role}`) : null;

  return (
    <>
      <PageHeader
        title={t('home.title', { name: user?.displayName ?? '' })}
        subtitle={role ? t('home.subtitleWithRole', { date, role }) : t('home.subtitle', { date })}
        actions={
          <>
            {can(PERMISSIONS.SAMPLES_RECEIVE) ? (
              <ActionButton priority="secondary" onPress={() => navigate('/samples/receive')}>
                {t('samples.receive')}
              </ActionButton>
            ) : null}
            {can(PERMISSIONS.REPORTS_PREPARE) ? (
              <ActionButton priority="primary" onPress={() => navigate('/reports/new')}>
                {t('nav.prepareReport')}
              </ActionButton>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-gutter lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {stages.length > 0 ? (
          <Panel title={t('home.stagesTitle')} description={t('home.stagesHint')}>
            <HorizontalBars
              data={stages}
              format={count}
              labelHeader={t('home.stageColumn')}
              valueHeader={t('home.countColumn')}
              caption={t('home.stagesTitle')}
            />
          </Panel>
        ) : null}

        {attention.length > 0 || mine.length > 0 ? (
          <Panel title={t('home.attentionTitle')} description={t('home.attentionHint')}>
            <div className="flex flex-col gap-stack">
              {attention.length > 0 ? <CountRows items={attention} /> : null}
              {mine.length > 0 ? (
                <div>
                  <h3 className="border-b border-default-200 pb-1 text-caption font-semibold text-default-700">
                    {t('home.myTasksTitle')}
                  </h3>
                  <CountRows items={mine} />
                </div>
              ) : null}
            </div>
          </Panel>
        ) : null}
      </div>

      <div className="mt-gutter">
        <QueuePanel data={data} format={count} />
      </div>

      <p className="mt-gutter text-caption text-default-600">{t('home.countsNotice')}</p>
    </>
  );
}

type QueueId = 'review' | 'approval' | 'returned' | 'mine' | 'unassigned' | 'inspect';

/**
 * ONE tabbed queue table. A tab exists only for a queue whose count the
 * summary sent — the same permission rule as the figures above — and only the
 * open tab is fetched, with the very request (and cache key) its own page
 * uses. The first page is shown; the full list is one link away.
 */
function QueuePanel({
  data,
  format,
}: {
  readonly data: DashboardSummary;
  readonly format: (value: number) => string;
}) {
  const { t } = useTranslation();

  const queues: { id: QueueId; label: string; total: number; to: string }[] = [];
  if (typeof data.awaitingReview === 'number') {
    queues.push({ id: 'review', label: t('nav.review'), total: data.awaitingReview, to: '/review' });
  }
  if (typeof data.awaitingApproval === 'number') {
    queues.push({ id: 'approval', label: t('nav.approval'), total: data.awaitingApproval, to: '/approval' });
  }
  if (typeof data.returnedToMe === 'number') {
    queues.push({ id: 'returned', label: t('nav.returnedWork'), total: data.returnedToMe, to: '/returned' });
  }
  if (data.myTasks) {
    queues.push({ id: 'mine', label: t('nav.myTasks'), total: data.myTasks.open, to: '/my-tasks' });
  }
  if (data.work) {
    queues.push({ id: 'unassigned', label: t('assignmentView.unassigned'), total: data.work.unassigned, to: '/work?view=unassigned' });
  }
  if (data.samples) {
    queues.push({ id: 'inspect', label: t('status.sample.awaiting_inspection'), total: data.samples.awaitingInspection, to: '/samples' });
  }

  const [chosen, setChosen] = useState<QueueId | null>(null);
  if (queues.length === 0) return null;
  const current =
    queues.find((queue) => queue.id === chosen) ??
    queues.find((queue) => queue.total > 0) ??
    queues[0];
  if (!current) return null;

  return (
    <Panel
      title={t('home.queuesTitle')}
      actions={
        <Link
          to={current.to}
          className="text-caption font-semibold text-primary-700 no-underline hover:underline"
        >
          {t('home.openFullList')}
        </Link>
      }
    >
      <div className="-mt-2 flex flex-col gap-stack">
        <ViewTabs
          label={t('home.queuesTitle')}
          idBase="home-queue"
          value={current.id}
          tabs={queues.map((queue) => ({ value: queue.id, label: queue.label, count: format(queue.total) }))}
          onChange={setChosen}
        />
        <ViewTabPanel idBase="home-queue" value={current.id}>
          <QueueTable id={current.id} label={current.label} />
        </ViewTabPanel>
      </div>
    </Panel>
  );
}

const FIRST = 1;

function QueueTable({ id, label }: { readonly id: QueueId; readonly label: string }) {
  switch (id) {
    case 'review':
      return <ReviewRows label={label} />;
    case 'approval':
      return <ApprovalRows label={label} />;
    case 'returned':
      return <ReturnedRows label={label} />;
    case 'mine':
      return <MyTaskRows label={label} />;
    case 'unassigned':
      return <UnassignedRows label={label} />;
    case 'inspect':
      return <InspectRows label={label} />;
  }
}

function ReviewRows({ label }: { readonly label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  // The review queue page's own request and key (ReviewQueuePage).
  const queue = useQuery({
    queryKey: ['review-queue', { page: FIRST }],
    queryFn: () => reviewApi.queue({ page: FIRST }),
  });
  type Row = NonNullable<typeof queue.data>['data'][number];
  const columns: readonly DataColumn<Row>[] = [
    { key: 'work', header: t('work.workColumn'), render: (row) => <WorkCell assignment={row.assignment} to={`/work/${row.assignmentId}`} /> },
    {
      key: 'performedBy',
      header: t('review.performedByColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">{row.performedBy.displayName}</span>
          {row.callerIsPerformer ? <StatusBadge label={t('review.ownWork')} tone="warning" size="sm" /> : null}
        </div>
      ),
    },
    {
      key: 'submitted',
      header: t('review.submittedColumn'),
      render: (row) => (
        <span className="text-caption text-default-700">{formatDateTime(row.submittedAt, { locale }) ?? row.submittedAt}</span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton
          priority={row.callerIsPerformer ? 'tertiary' : 'secondary'}
          size="sm"
          // The server refuses self-review (D107); this only avoids a dead end.
          isDisabled={row.callerIsPerformer}
          onPress={() => navigate(`/review/${row.attemptId}`)}
        >
          {t('review.reviewAction')}
        </ActionButton>
      ),
    },
  ];
  return (
    <DataTable
      label={label}
      columns={columns}
      rows={queue.data?.data ?? []}
      rowKey={(row) => row.attemptId}
      isLoading={queue.isLoading}
      error={queue.error}
      onRetry={() => void queue.refetch()}
      emptyContent={t('review.none')}
    />
  );
}

function ApprovalRows({ label }: { readonly label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  // The approval queue page's own request and key (ApprovalQueuePage).
  const queue = useQuery({
    queryKey: ['approval-queue', { page: FIRST }],
    queryFn: () => approvalApi.queue({ page: FIRST }),
  });
  type Row = NonNullable<typeof queue.data>['data'][number];
  const columns: readonly DataColumn<Row>[] = [
    { key: 'work', header: t('work.workColumn'), render: (row) => <WorkCell assignment={row.assignment} to={`/work/${row.assignmentId}`} /> },
    {
      key: 'reviewed',
      header: t('approval.reviewedColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">{row.technicallyAcceptedBy.displayName}</span>
          <span className="block text-caption text-default-600">
            {formatDateTime(row.technicallyAcceptedAt, { locale }) ?? row.technicallyAcceptedAt}
          </span>
        </div>
      ),
    },
    {
      key: 'performedBy',
      header: t('review.performedByColumn'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block text-default-700">{row.performedBy.displayName}</span>
          {row.callerIsPerformer ? <StatusBadge label={t('review.ownWork')} tone="warning" size="sm" /> : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton
          priority={row.callerIsPerformer ? 'tertiary' : 'secondary'}
          size="sm"
          // The server refuses self-approval (D107).
          isDisabled={row.callerIsPerformer}
          onPress={() => navigate(`/approval/${row.attemptId}`)}
        >
          {t('approval.openAction')}
        </ActionButton>
      ),
    },
  ];
  return (
    <DataTable
      label={label}
      columns={columns}
      rows={queue.data?.data ?? []}
      rowKey={(row) => row.attemptId}
      isLoading={queue.isLoading}
      error={queue.error}
      onRetry={() => void queue.refetch()}
      emptyContent={t('approval.none')}
    />
  );
}

function ReturnedRows({ label }: { readonly label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The returned-work page's own request and key (ReturnedWorkPage).
  const returned = useQuery({
    queryKey: ['returned-work', { page: FIRST }],
    queryFn: () => returnedWorkApi.list({ page: FIRST }),
  });
  type Row = NonNullable<typeof returned.data>['data'][number];
  const columns: readonly DataColumn<Row>[] = [
    { key: 'work', header: t('work.workColumn'), render: (row) => <WorkCell assignment={row.assignment} to={`/work/${row.assignmentId}`} /> },
    {
      key: 'stage',
      header: t('returnedWork.stageColumn'),
      render: (row) => <StatusBadge label={t(`returnStage.${row.returnStage}`)} tone="warning" size="sm" />,
    },
    {
      key: 'reason',
      // Verbatim and uncut, as on the returned-work page.
      header: t('returnedWork.reasonColumn'),
      render: (row) => (
        <p className="min-w-48 max-w-prose whitespace-pre-line break-words text-caption text-default-800" dir="auto">
          {row.reason}
        </p>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton priority="secondary" size="sm" onPress={() => navigate(`/attempts/${row.attemptId}`)}>
          {t('returnedWork.correctAction')}
        </ActionButton>
      ),
    },
  ];
  return (
    <DataTable
      label={label}
      columns={columns}
      rows={returned.data?.data ?? []}
      rowKey={(row) => row.attemptId}
      isLoading={returned.isLoading}
      error={returned.error}
      onRetry={() => void returned.refetch()}
      emptyContent={t('returnedWork.none')}
    />
  );
}

function MyTaskRows({ label }: { readonly label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // My Tasks' own request and key (MyTasksPage).
  const tasks = useQuery({
    queryKey: ['my-tasks', { page: FIRST }],
    queryFn: () => assignmentsApi.myTasks({ page: FIRST }),
  });
  type Row = NonNullable<typeof tasks.data>['data'][number];
  const columns: readonly DataColumn<Row>[] = [
    { key: 'work', header: t('work.workColumn'), render: (row) => <WorkCell assignment={row} to={`/work/${row.id}`} /> },
    { key: 'dates', header: t('work.datesColumn'), render: (row) => <AssignmentDates assignment={row} /> },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <StatusBadge label={t(`status.test.${row.status}`)} tone={toneForAssignmentStatus(row.status)} size="sm" />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) =>
        row.openAttemptId ? (
          <ActionButton priority="secondary" size="sm" onPress={() => navigate(`/attempts/${row.openAttemptId}`)}>
            {t('myTasks.continueDraft')}
          </ActionButton>
        ) : (
          <ActionButton priority="tertiary" size="sm" onPress={() => navigate(`/work/${row.id}`)}>
            {t('common.open')}
          </ActionButton>
        ),
    },
  ];
  return (
    <DataTable
      label={label}
      columns={columns}
      rows={tasks.data?.data ?? []}
      rowKey={(row) => row.id}
      isLoading={tasks.isLoading}
      error={tasks.error}
      onRetry={() => void tasks.refetch()}
      emptyContent={t('myTasks.none')}
    />
  );
}

function UnassignedRows({ label }: { readonly label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const view = 'unassigned';
  // The work list's own request and key for its "unassigned" view (WorkListPage).
  const list = useQuery({
    queryKey: ['assignments', { view, search: '', page: FIRST }],
    queryFn: () => assignmentsApi.list({ view, page: FIRST }),
  });
  type Row = NonNullable<typeof list.data>['data'][number];
  const columns: readonly DataColumn<Row>[] = [
    { key: 'work', header: t('work.workColumn'), render: (row) => <WorkCell assignment={row} to={`/work/${row.id}`} /> },
    {
      key: 'priority',
      header: t('work.priorityColumn'),
      render: (row) => <span className="text-default-700">{t(`workOrderPriority.${row.priority}`)}</span>,
    },
    { key: 'dates', header: t('work.datesColumn'), render: (row) => <AssignmentDates assignment={row} /> },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton priority="tertiary" size="sm" onPress={() => navigate(`/work/${row.id}`)}>
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];
  return (
    <DataTable
      label={label}
      columns={columns}
      rows={list.data?.data ?? []}
      rowKey={(row) => row.id}
      isLoading={list.isLoading}
      error={list.error}
      onRetry={() => void list.refetch()}
      emptyContent={t('work.noneFound')}
    />
  );
}

function InspectRows({ label }: { readonly label: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const status = 'awaiting_inspection';
  // The sample register's own request and key, with its status filter set
  // (SamplesPage) — so the two share one cache entry.
  const samples = useQuery({
    queryKey: [
      'samples',
      {
        page: FIRST,
        search: '',
        status,
        sampleType: null,
        includeDisposed: false,
        dueForDisposal: false,
        workOrderId: null,
        showVoided: false,
      },
    ],
    queryFn: () => samplesApi.list({ page: FIRST, status }),
  });
  type Row = NonNullable<typeof samples.data>['data'][number];
  const columns: readonly DataColumn<Row>[] = [
    {
      key: 'sample',
      header: t('samples.sampleId'),
      render: (row) => (
        <Link
          to={`/samples/${row.id}`}
          className="measure font-medium text-default-900 no-underline hover:text-primary-700 hover:underline"
          dir="ltr"
        >
          {row.sampleNumber}
        </Link>
      ),
    },
    {
      key: 'received',
      header: t('home.receivedColumn'),
      render: (row) => (
        <span className="text-caption text-default-700">{formatDateTime(row.receivedAt, { locale }) ?? row.receivedAt}</span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <StatusBadge label={t(`status.sample.${row.status}`)} tone={toneForSampleStatus(row.status)} size="sm" />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton priority="tertiary" size="sm" onPress={() => navigate(`/samples/${row.id}`)}>
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];
  return (
    <DataTable
      label={label}
      columns={columns}
      rows={samples.data?.data ?? []}
      rowKey={(row) => row.id}
      isLoading={samples.isLoading}
      error={samples.error}
      onRetry={() => void samples.refetch()}
      emptyContent={t('home.noneToInspect')}
    />
  );
}
